import { useAuthFetch } from "components/Login/hook";
import { useGuardedEffect } from "hooks/useGuardedEffect";
import { useCallback, useState } from "react";

export type McpClient = {
  readonly createdAt?: number;
  readonly createdBy?: {
    readonly ip?: string;
    readonly user?: string;
  };
  readonly id: string;
  readonly hostname: string;
  readonly platform: string;
  readonly redirectUri: string;
  readonly version: string;
};

type McpClientsResp = {
  clients: McpClient[];
};

/**
 * Fetches registered MCP clients through the backend so the UI never reads
 * Firestore directly or receives stored client secret hashes.
 */
export const useMcpClients = (): {
  clients: McpClient[];
  loading: boolean;
} => {
  const authFetch = useAuthFetch();
  const [clients, setClients] = useState<McpClient[]>([]);
  const [loading, setLoading] = useState(false);

  useGuardedEffect(
    (cancellation) => async () => {
      setLoading(true);
      try {
        const res = await authFetch("mcp/clients", {
          method: "GET",
          cancellation,
        });
        if (!res || cancellation.isCancelled) return;
        const body = (await res.json()) as McpClientsResp;
        if (cancellation.isCancelled) return;
        setClients(body.clients ?? []);
      } finally {
        if (!cancellation.isCancelled) setLoading(false);
      }
    },
    [authFetch],
    useCallback(() => {
      setClients([]);
      setLoading(false);
    }, [])
  );

  return { clients, loading };
};
